import type { components } from "@roastery/api-client";
import { useApi } from "@roastery/auth";
import { useQueryClient } from "@tanstack/react-query";

export type ProductUnitCost = components["schemas"]["ProductUnitCost"];
export type ProductUnitCostMaterial = components["schemas"]["ProductUnitCostMaterial"];
export type VariantCost = components["schemas"]["VariantCost"];

const BATCH_COSTS_PATH = "/api/v1/costing/batches/{batchId}/product-costs";
const VARIANT_COSTS_PATH = "/api/v1/costing/variants/costs";

/**
 * What every bag a batch produced cost (US-12.4). Asked for only once the batch has been packed —
 * before that the answer is an empty list, and an empty card is worse than no card.
 */
export function useBatchProductCosts(batchId: string, packed: boolean) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    BATCH_COSTS_PATH,
    { params: { path: { batchId } } },
    { enabled: packed },
  );
}

/** Last and average COGS of the given SKUs; variants nothing has packed yet are simply absent. */
export function useVariantCosts(variantIds: string[]) {
  const api = useApi();
  return api.query.useQuery(
    "get",
    VARIANT_COSTS_PATH,
    { params: { query: { variantId: variantIds } } },
    { enabled: variantIds.length > 0 },
  );
}

/** A repriced batch changes what the bags packed from it cost, so both reads move together. */
export function useInvalidateProductCosts() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["get", BATCH_COSTS_PATH] });
    await queryClient.invalidateQueries({ queryKey: ["get", VARIANT_COSTS_PATH] });
  };
}
